import type React from "react";
import { useCallback } from "react";
import { AnimationProvider } from "@/features/animation";
import { AuthProvider } from "@/features/auth";
import { PreviewFrame, Timeline } from "@/features/preview";
import { ProjectListProvider, ProjectProvider } from "@/features/project";
import { Tab, TabProvider, TabView } from "@/features/Tab";
import { UIProvider, useUI, useUIValue } from "@/features/ui";
import {
  AgentDiscoveryModal,
  AssetsPanel,
  ChatPanel,
  CodeEditor,
  ProjectsPanel,
  StateEditor,
  Toolbar,
  TopUpModal,
} from "./components";

function Studio({ children }: React.PropsWithChildren) {
  const ui = useUIValue();
  const handleInsufficientCredits = useCallback(() => {
    ui.setShowTopUpModal(true);
  }, [ui.setShowTopUpModal]);

  return (
    <AuthProvider>
      <UIProvider value={ui}>
        <ProjectListProvider>
          <ProjectProvider onInsufficientCredits={handleInsufficientCredits}>
            <TabProvider>
              <AnimationProvider>
                <div className="h-dvh min-h-(--min-size) w-full flex bg-slate-950 overflow-hidden text-slate-200 font-sans">
                  {children}
                </div>
              </AnimationProvider>
            </TabProvider>
          </ProjectProvider>
        </ProjectListProvider>
      </UIProvider>
    </AuthProvider>
  );
}

function Sidebar({ children }: React.PropsWithChildren) {
  const { showChat, setShowChat } = useUI();

  return (
    <>
      {/* Mobile overlay backdrop - only for animation */}
      <div
        className={`fixed inset-0 bg-black/50 z-30 sbs:hidden transition-opacity duration-300 ${showChat ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setShowChat(false)}
        onKeyDown={(e) => e.key === "Escape" && setShowChat(false)}
        role="button"
        tabIndex={showChat ? 0 : -1}
        aria-label="Close sidebar"
      />
      {/* Sidebar container - full width on mobile, fixed width on wider screens */}
      <div
        className={`fixed inset-0 z-40 flex transition-all duration-300 ease-out sbs:relative sbs:inset-auto sbs:z-auto ${showChat ? "translate-x-0" : "-translate-x-full sbs:translate-x-0 sbs:-ml-[28rem]"}`}
      >
        {children}
      </div>
    </>
  );
}

function Workspace({ children }: React.PropsWithChildren) {
  return <div className="flex-1 flex flex-col min-w-0 bg-black">{children}</div>;
}

const PreviewTab: React.FC = () => (
  <div className="absolute inset-0 flex flex-col">
    <PreviewFrame />
    <Timeline />
  </div>
);

export function StudioView() {
  return (
    <Studio>
      <Sidebar>
        <ProjectsPanel />
        <ChatPanel />
      </Sidebar>
      <Workspace>
        <Toolbar />
        <Tab>
          <TabView name="code" component={CodeEditor} />
          <TabView name="assets" component={AssetsPanel} />
          <TabView name="preview" component={PreviewTab} />
          <TabView name="state" component={StateEditor} />
        </Tab>
      </Workspace>
      <TopUpModal />
      <AgentDiscoveryModal />
    </Studio>
  );
}
